import classnames from 'classnames';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  CircularProgressBar,
  CloseButton,
  Loader,
  Text,
  TextButton,
  Tooltip,
  WixIcons,
  ExternalLink,
} from '../../../../components/common';
import { MainSource } from '../../../../constants/Source';
import { stopPropagation } from '../../../../utils/dom/domEvents';
import { useTrackAddMediaSideBarActionsAnalytics } from '../../../../utils/hooks/bi/useTrackAddMediaSideBarActionsAnalytics';
import { useUploadQueueHandlers } from '../../../../utils/hooks/useFilesUploader';
import { useScrollStream } from '../../../../utils/hooks/useScrollStream';
import { memoizedOver } from '../../../../utils/memoize';
import { getItemFromServerResponse } from '../../../../utils/uploadingFile';
import { UploadingFileError } from '../UploadingFileError';
import { UploadingFileStatus } from '../UploadingFileStatus';
import { UploadingFileThumbnail } from '../UploadingFileThumbnail';
import styles from './UploadingFile.scss';
import { IOuter } from './UploadingFile.types';
import { useUploadingFileProps } from './useUploadingFileProps';
import { isPremiumError } from '../UploadingFileError/premiumErrors';
import { usePremium } from './usePremium';

export const UploadingFile: FC<IOuter> = ({ className, uploadingFile, style = {}, onOpenDestinationFolder }) => {
  const {
    isPending,
    isInProgress,
    canBeAborted,
    isCanceled,
    uploadError,
    readableFileSize,
    isProcessing,
    isInvalid,
    isUploaded,
    uploadProgressPercent,
    isFailed,
    canRetry,
    canResume,
    cancelFileUploading,
    retryToUpload,
    resumeUpload,
  } = useUploadingFileProps(uploadingFile);

  const { upgradeUrl } = usePremium();

  const { removeFileFromUploadingQueue } = useUploadQueueHandlers();

  const {
    trackAddMediaSideBarActionClear,
    trackAddMediaSideBarActionRetry,
    trackAddMediaSideBarActionGoToFile,
    trackAddMediaSideBarActionResume,
  } = useTrackAddMediaSideBarActionsAnalytics();

  const handleRemoveFileFromUploadingQueue = useCallback(
    (event) => {
      removeFileFromUploadingQueue(uploadingFile);
      trackAddMediaSideBarActionClear();
      event.stopPropagation();
    },
    [removeFileFromUploadingQueue, uploadingFile, trackAddMediaSideBarActionClear],
  );

  const { emitScroll } = useScrollStream();

  const handleOpenDestinationFolder = useCallback(() => {
    if (!isUploaded) {
      return false;
    }

    const destinationFolderId = uploadingFile.source.folderId;
    const fileId = getItemFromServerResponse(uploadingFile).id;

    onOpenDestinationFolder(destinationFolderId, MainSource.SiteFiles, fileId);
    setTimeout(() => {
      emitScroll(fileId);
    }, 0);
    trackAddMediaSideBarActionGoToFile();
  }, [onOpenDestinationFolder, trackAddMediaSideBarActionGoToFile, emitScroll, uploadingFile]);

  return (
    <div
      className={classnames(
        styles.file,
        {
          [styles.canBeAborted]: canBeAborted,
          [styles.invalid]: isFailed,
          [styles.pending]: isPending,
          [styles.succeeded]: isUploaded,
        },
        className,
      )}
      style={style}
      onClick={handleOpenDestinationFolder}
      data-hook="uploading-file"
    >
      <UploadingFileThumbnail
        className={styles.thumbnail}
        uploadingFile={uploadingFile}
        fallbackImageClassName={styles.fallbackImageClassName}
      />

      <div className={styles.name} data-hook="uploading-file-name">
        <Text size="small" weight="bold" ellipsis appendTo="scrollParent">
          {uploadingFile.source.file.name}
        </Text>

        {isFailed && (
          <Text
            className={styles.error}
            size="tiny"
            weight="thin"
            skin={isPremiumError(uploadError) ? 'standard' : 'error'}
            dataHook="uploading-file-error-message"
          >
            {isCanceled ? (
              <FormattedMessage id="uploading-file.status.canceled" />
            ) : isInvalid ? (
              <UploadingFileError uploadError={uploadError} translationsPrefix="upload-error." />
            ) : null}
          </Text>
        )}
      </div>

      <UploadingFileStatus
        className={styles.status}
        status={uploadingFile.status}
        readableFileSize={readableFileSize}
      />

      <div className={styles.actions} data-hook="uploading-file-actions-performer">
        {canBeAborted && !isCanceled && (
          <Tooltip content={<FormattedMessage id="uploading-file.cancel" />} appendTo="viewport">
            <CloseButton
              className={classnames(styles.cancelButton, styles.blueAndRedButton)}
              onClick={memoizedOver(cancelFileUploading, stopPropagation)}
              dataHook="uploading-file-cancel-button"
            />
          </Tooltip>
        )}

        {isInProgress && !isFailed && (
          <span className={styles.loaderScaly}>
            {uploadProgressPercent ? (
              <CircularProgressBar
                size="small"
                value={uploadProgressPercent * 100}
                dataHook="uploading-file-percentage"
              />
            ) : (
              <Loader size="small" className={styles.inProgressLoader} dataHook="uploading-file-loader" />
            )}
          </span>
        )}

        {isProcessing && !isFailed && (
          <span className={styles.loaderScaly}>
            <Loader size="small" dataHook="uploading-file-loader" />
          </span>
        )}

        {isUploaded && (
          <>
            <WixIcons.StatusCompleteFilled className={styles.doneIcon} data-hook="uploading-file-done-icon" />

            <Tooltip
              content={<FormattedMessage id="uploading-file.go-to-destination-button-hint" />}
              maxWidth={147}
              enterDelay={0}
              appendTo="viewport"
            >
              <span className={styles.goToDestinationIcon} data-hook="uploading-file-go-to-destination-folder-button">
                <WixIcons.ChevronRightSmall />
              </span>
            </Tooltip>
          </>
        )}

        {isFailed && (
          <>
            {isPremiumError(uploadError) ? (
              <Tooltip content={<FormattedMessage id="uploading-file.upgrade" />} appendTo="viewport">
                <ExternalLink href={upgradeUrl}>
                  <WixIcons.Premium className={classnames(styles.errorIcon, styles.upgradeButton)} />
                </ExternalLink>
              </Tooltip>
            ) : (
              <WixIcons.StatusAlertFilled className={styles.errorIcon} data-hook="uploading-file-error-icon" />
            )}
            <span className={styles.errorAction}>
              {canResume && canRetry ? (
                <TextButton
                  as="a"
                  onClick={memoizedOver(trackAddMediaSideBarActionResume, resumeUpload, stopPropagation)}
                  size="small"
                  weight="normal"
                  underline="onHover"
                  dataHook="uploading-file-resume-button"
                >
                  <FormattedMessage id="uploading-file.resume" />
                </TextButton>
              ) : canRetry ? (
                <TextButton
                  as="a"
                  onClick={memoizedOver(trackAddMediaSideBarActionRetry, retryToUpload, stopPropagation)}
                  size="small"
                  weight="normal"
                  underline="onHover"
                  dataHook="uploading-file-retry-button"
                >
                  <FormattedMessage id="uploading-file.retry" />
                </TextButton>
              ) : (
                <Tooltip
                  content={<FormattedMessage id="uploading-file.clear-item-button-hint" />}
                  enterDelay={0}
                  appendTo="viewport"
                >
                  <CloseButton
                    className={styles.blueAndRedButton}
                    onClick={handleRemoveFileFromUploadingQueue}
                    dataHook="uploading-file-clear-item-button"
                  />
                </Tooltip>
              )}
            </span>
          </>
        )}
      </div>
    </div>
  );
};
